// Supabase Realtime adapter — implements MultiplayerPort over channel
// broadcast + presence ("Gemeinsam zeichnen").
//
// Why broadcast (not Postgres changes): collaborative strokes are ephemeral,
// high-frequency and need no persistence — broadcast relays them peer-to-peer
// through the Realtime server with no database round-trip and no RLS to manage.
// Presence gives the live participant count for free.
//
// Like SupabaseAuth, this depends only on a narrow structural slice of the
// supabase-js client (`SupabaseRealtimeLike`), so it is unit-testable with a fake
// and never imports the SDK type surface. Mirrors the chatpartygames `canvas:*`
// socket protocol so both implementations stay semantically identical.

import type { IdProvider, MultiplayerPort } from '../bridge/ports';
import {
  MP_EVENTS,
  roomChannelName,
  sanitizeRoomId,
  type RemoteClear,
  type RemoteStrokeEnd,
  type RemoteStrokeStart,
  type RemoteStrokeUpdate,
  type RemoteUndo,
  type StrokeStartInput,
  type StrokeUpdateInput,
} from '../domain/multiplayer';

// ---- narrow structural view of the supabase realtime client we use ----------

type BroadcastMessage = { event: string; type: string; payload: Record<string, unknown> };
type BroadcastHandler = (message: BroadcastMessage) => void;
type PresenceHandler = () => void;

export interface RealtimeChannelLike {
  on(type: 'broadcast', filter: { event: string }, cb: BroadcastHandler): RealtimeChannelLike;
  on(
    type: 'presence',
    filter: { event: 'sync' | 'join' | 'leave' },
    cb: PresenceHandler,
  ): RealtimeChannelLike;
  subscribe(cb?: (status: string) => void): RealtimeChannelLike;
  send(args: {
    type: 'broadcast';
    event: string;
    payload: Record<string, unknown>;
  }): Promise<unknown>;
  track(state: Record<string, unknown>): Promise<unknown>;
  presenceState(): Record<string, unknown[]>;
  unsubscribe(): Promise<unknown>;
}

export interface SupabaseRealtimeLike {
  channel(
    name: string,
    opts?: {
      config?: { broadcast?: { self?: boolean; ack?: boolean }; presence?: { key?: string } };
    },
  ): RealtimeChannelLike;
  removeChannel(channel: RealtimeChannelLike): Promise<unknown>;
}

// Terminal subscribe() statuses — settle join() so the monolith never hangs.
const TERMINAL_STATUSES = new Set(['SUBSCRIBED', 'CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED']);

export class SupabaseRealtime implements MultiplayerPort {
  private channel: RealtimeChannelLike | null = null;
  private room: string | null = null;
  private subscribed = false;
  private readonly id: string;

  private readonly startCbs = new Set<(s: RemoteStrokeStart) => void>();
  private readonly updateCbs = new Set<(s: RemoteStrokeUpdate) => void>();
  private readonly endCbs = new Set<(s: RemoteStrokeEnd) => void>();
  private readonly clearCbs = new Set<(c: RemoteClear) => void>();
  private readonly undoCbs = new Set<(u: RemoteUndo) => void>();
  private readonly participantsCbs = new Set<(n: number) => void>();

  constructor(
    private readonly client: SupabaseRealtimeLike,
    idProvider: IdProvider,
  ) {
    this.id = idProvider.newId();
  }

  clientId(): string {
    return this.id;
  }
  isActive(): boolean {
    return this.channel !== null && this.subscribed;
  }
  roomId(): string | null {
    return this.room;
  }

  async join(roomId: string): Promise<void> {
    const safe = sanitizeRoomId(roomId);
    if (!safe) throw new Error('multiplayer_join: invalid room id');
    if (this.channel) await this.leave();
    this.room = safe;

    const ch = this.client.channel(roomChannelName(safe), {
      config: { broadcast: { self: false }, presence: { key: this.id } },
    });

    ch.on('broadcast', { event: MP_EVENTS.strokeStart }, (m) => this.dispatchStart(m.payload));
    ch.on('broadcast', { event: MP_EVENTS.strokeUpdate }, (m) => this.dispatchUpdate(m.payload));
    ch.on('broadcast', { event: MP_EVENTS.strokeEnd }, (m) => this.dispatchEnd(m.payload));
    ch.on('broadcast', { event: MP_EVENTS.clear }, (m) => this.dispatchClear(m.payload));
    ch.on('broadcast', { event: MP_EVENTS.undo }, (m) => this.dispatchUndo(m.payload));
    ch.on('presence', { event: 'sync' }, () => this.emitParticipants());
    ch.on('presence', { event: 'join' }, () => this.emitParticipants());
    ch.on('presence', { event: 'leave' }, () => this.emitParticipants());

    this.channel = ch;

    await new Promise<void>((resolve) => {
      let settled = false;
      ch.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.subscribed = true;
          void ch.track({ id: this.id });
        }
        if (!settled && TERMINAL_STATUSES.has(status)) {
          settled = true;
          resolve();
        }
      });
    });
  }

  async leave(): Promise<void> {
    const ch = this.channel;
    this.channel = null;
    this.room = null;
    this.subscribed = false;
    if (ch) {
      try {
        await this.client.removeChannel(ch);
      } catch {
        // best-effort teardown — a failed removeChannel must not surface to UI.
      }
    }
  }

  // ---- send ----------------------------------------------------------------
  sendStrokeStart(input: StrokeStartInput): void {
    this.broadcast(MP_EVENTS.strokeStart, { ...input, senderId: this.id });
  }
  sendStrokeUpdate(input: StrokeUpdateInput): void {
    this.broadcast(MP_EVENTS.strokeUpdate, { ...input, senderId: this.id });
  }
  sendStrokeEnd(input: { strokeId: string }): void {
    this.broadcast(MP_EVENTS.strokeEnd, { strokeId: input.strokeId, senderId: this.id });
  }
  sendClear(): void {
    this.broadcast(MP_EVENTS.clear, { senderId: this.id });
  }
  sendUndo(strokeId: string): void {
    this.broadcast(MP_EVENTS.undo, { strokeId, senderId: this.id });
  }

  private broadcast(event: string, payload: Record<string, unknown>): void {
    const ch = this.channel;
    if (!ch || !this.subscribed) return; // not in a live room → drop silently
    void ch.send({ type: 'broadcast', event, payload });
  }

  // ---- subscribe -----------------------------------------------------------
  onStrokeStart(cb: (s: RemoteStrokeStart) => void): () => void {
    this.startCbs.add(cb);
    return () => this.startCbs.delete(cb);
  }
  onStrokeUpdate(cb: (s: RemoteStrokeUpdate) => void): () => void {
    this.updateCbs.add(cb);
    return () => this.updateCbs.delete(cb);
  }
  onStrokeEnd(cb: (s: RemoteStrokeEnd) => void): () => void {
    this.endCbs.add(cb);
    return () => this.endCbs.delete(cb);
  }
  onClear(cb: (c: RemoteClear) => void): () => void {
    this.clearCbs.add(cb);
    return () => this.clearCbs.delete(cb);
  }
  onUndo(cb: (u: RemoteUndo) => void): () => void {
    this.undoCbs.add(cb);
    return () => this.undoCbs.delete(cb);
  }
  onParticipants(cb: (count: number) => void): () => void {
    this.participantsCbs.add(cb);
    return () => this.participantsCbs.delete(cb);
  }

  // ---- inbound dispatch (defensive: payloads come off the wire) ------------
  private dispatchStart(p: Record<string, unknown>): void {
    if (typeof p.strokeId !== 'string') return;
    const s = p as unknown as RemoteStrokeStart;
    this.startCbs.forEach((cb) => cb(s));
  }
  private dispatchUpdate(p: Record<string, unknown>): void {
    if (typeof p.strokeId !== 'string' || !Array.isArray(p.points)) return;
    const s = p as unknown as RemoteStrokeUpdate;
    this.updateCbs.forEach((cb) => cb(s));
  }
  private dispatchEnd(p: Record<string, unknown>): void {
    if (typeof p.strokeId !== 'string') return;
    const s = p as unknown as RemoteStrokeEnd;
    this.endCbs.forEach((cb) => cb(s));
  }
  private dispatchClear(p: Record<string, unknown>): void {
    const c = p as unknown as RemoteClear;
    this.clearCbs.forEach((cb) => cb(c));
  }
  private dispatchUndo(p: Record<string, unknown>): void {
    if (typeof p.strokeId !== 'string') return;
    const u = p as unknown as RemoteUndo;
    this.undoCbs.forEach((cb) => cb(u));
  }

  private emitParticipants(): void {
    const ch = this.channel;
    if (!ch) return;
    const count = Object.keys(ch.presenceState()).length;
    this.participantsCbs.forEach((cb) => cb(count));
  }
}

/**
 * No-op multiplayer transport used when no backend is configured. Every call is
 * inert and `isActive()` stays false, so the monolith can detect that
 * collaborative drawing is unavailable and keep working solo (graceful
 * degradation — the app never crashes on a missing Supabase config).
 */
export class NullMultiplayer implements MultiplayerPort {
  private readonly id: string;
  constructor(idProvider: IdProvider) {
    this.id = idProvider.newId();
  }
  async join(_roomId: string): Promise<void> {
    /* no backend — silently unavailable */
  }
  async leave(): Promise<void> {
    /* no-op */
  }
  isActive(): boolean {
    return false;
  }
  roomId(): string | null {
    return null;
  }
  clientId(): string {
    return this.id;
  }
  sendStrokeStart(_input: StrokeStartInput): void {}
  sendStrokeUpdate(_input: StrokeUpdateInput): void {}
  sendStrokeEnd(_input: { strokeId: string }): void {}
  sendClear(): void {}
  sendUndo(_strokeId: string): void {}
  onStrokeStart(_cb: (s: RemoteStrokeStart) => void): () => void {
    return () => {};
  }
  onStrokeUpdate(_cb: (s: RemoteStrokeUpdate) => void): () => void {
    return () => {};
  }
  onStrokeEnd(_cb: (s: RemoteStrokeEnd) => void): () => void {
    return () => {};
  }
  onClear(_cb: (c: RemoteClear) => void): () => void {
    return () => {};
  }
  onUndo(_cb: (u: RemoteUndo) => void): () => void {
    return () => {};
  }
  onParticipants(_cb: (count: number) => void): () => void {
    return () => {};
  }
}
